import { Injectable, OnDestroy } from '@angular/core';
import { Subject, timer, takeUntil, firstValueFrom, Subscription } from 'rxjs';
import { LogApiService } from './log-api.service';
import { ILogEntry } from 'src/app/interfaces/ilog-entry';

@Injectable({
  providedIn: 'root',
})
export class LogBufferService implements OnDestroy {
  private buffer: ILogEntry[] = [];
  private readonly flushInterval = 60000; // 60s
  private readonly maxBufferSize = 50;
  private readonly localStorageKey = 'loggerBuffer';
  private isFlushing = false;

  private destroy$ = new Subject<void>();
  private timerSubscription: Subscription | undefined;

  constructor(private logApiService: LogApiService) {
    this.restoreBuffer();
    this.startTimer();
  }

  enqueue(entry: ILogEntry) {
    this.buffer.push(entry);
    if (this.buffer.length >= this.maxBufferSize) {
      this.flush();
    }
  }

  private startTimer() {
    this.timerSubscription = timer(this.flushInterval, this.flushInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.flush());
  }

  private resetTimer() {
    this.timerSubscription?.unsubscribe(); // unsubscribe if it exists
    this.startTimer();
  }

  async flush() {
    if (this.isFlushing || this.buffer.length === 0) return;

    this.isFlushing = true;
    const batch = this.buffer.splice(0, this.buffer.length);

    try {
      await firstValueFrom(this.logApiService.saveLogs(batch));
    } catch (err) {
      console.warn('Failed to send logs, persisting to localStorage', err);
      this.persistBuffer(batch);
    } finally {
      this.isFlushing = false;
      this.resetTimer();
    }
  }

  private persistBuffer(batch: ILogEntry[]) {
    try {
      const existing = this.getPersistedBuffer();
      localStorage.setItem(
        this.localStorageKey,
        JSON.stringify(existing.concat(batch))
      );
    } catch (err) {
      console.error('Failed to persist logs', err);
    }
  }

  private getPersistedBuffer(): ILogEntry[] {
    try {
      const raw = localStorage.getItem(this.localStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private restoreBuffer() {
    const persisted = this.getPersistedBuffer();
    if (persisted.length) {
      this.buffer = persisted;
      localStorage.removeItem(this.localStorageKey);
      this.flush();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.flush();
  }
}
